import { Link } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-indigo-500" />
            </div>
            <span className="text-xl font-bold text-indigo-500">ClientKit</span>
          </Link>
          <Link to="/" className="text-sm text-indigo-400 hover:text-indigo-500 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-indigo-500 mb-2">Privacy Policy</h1>
        <p className="text-slate-400 mb-8">Last updated: January 23, 2026</p>

        <div className="prose prose-invert prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">1. Introduction</h2>
            <p>
              ClientKit ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our freelance business management platform at yourfreelanceros.com (the "Service").
            </p>
            <p>
              Please read this privacy policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">2.1 Personal Information</h3>
            <p>We may collect personal information that you voluntarily provide when using our Service, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name and email address</li>
              <li>Account credentials (encrypted)</li>
              <li>Business information (company name, profession)</li>
              <li>Payment information (processed securely via Stripe)</li>
              <li>Profile picture (if provided via Google OAuth)</li>
            </ul>

            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">2.2 Client Data</h3>
            <p>When you use our Service to manage your freelance business, you may input:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Client contact information</li>
              <li>Project details and communications</li>
              <li>Proposals, contracts, and invoices</li>
              <li>Email templates and scheduled messages</li>
            </ul>

            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">2.3 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Device and browser information</li>
              <li>IP address and general location</li>
              <li>Usage data and analytics</li>
              <li>Cookies and similar technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">3. How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative notifications and updates</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Generate AI-powered content (using anonymized prompts)</li>
              <li>Monitor and analyze usage patterns to improve user experience</li>
              <li>Detect, prevent, and address technical issues and fraud</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">4. Third-Party Services</h2>
            <p>We integrate with the following third-party services:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Google OAuth:</strong> For authentication and optional Gmail integration</li>
              <li><strong>Stripe:</strong> For secure payment processing</li>
              <li><strong>SendGrid:</strong> For transactional emails</li>
              <li><strong>OpenAI:</strong> For AI-powered content generation</li>
              <li><strong>MongoDB:</strong> For secure data storage</li>
            </ul>
            <p className="mt-2">
              Each third-party service has its own privacy policy governing the use of your information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">5. Gmail Integration</h2>
            <p>
              If you choose to connect your Gmail account, we request access to send emails on your behalf. We:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Only use the <code className="bg-slate-800 px-1 rounded">gmail.send</code> scope to send emails you explicitly request</li>
              <li>Do not read, store, or access your inbox or other emails</li>
              <li>Store only the OAuth tokens required to send emails (encrypted)</li>
              <li>Allow you to disconnect Gmail at any time from your settings</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">6. Data Security</h2>
            <p>
              We implement industry-standard security measures including:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>SSL/TLS encryption for all data in transit</li>
              <li>Encrypted storage for sensitive data (passwords, tokens)</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">7. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to provide you services. You may request deletion of your data at any time by contacting us at hello@yourfreelanceros.com.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">8. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access and receive a copy of your personal data</li>
              <li>Rectify or update inaccurate personal data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability (receive your data in a structured format)</li>
              <li>Withdraw consent at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">9. Children's Privacy</h2>
            <p>
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <ul className="list-none pl-0 mt-2">
              <li><strong>Email:</strong> hello@yourfreelanceros.com</li>
              <li><strong>Website:</strong> yourfreelanceros.com</li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">© 2026 ClientKit. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-sm text-indigo-400 hover:text-indigo-500">Privacy Policy</Link>
            <Link to="/terms" className="text-sm text-indigo-400 hover:text-indigo-500">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
